import { useCallback, useEffect, useState } from "react";
import { MdDelete, MdEdit } from "react-icons/md";
import { toast } from "react-toastify";
import AddProductModal from "./AddProductModal";
import EditProductModal from "./EditProductModal";

export default function ProductTable() {
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editableProductId, setEditableProductId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(""); // Track selected category
  const [searchTerm, setSearchTerm] = useState(""); // Track search input

  const toggleAddProduct = () => {
    setShowAddProduct(!showAddProduct);
    if (showAddProduct) {
      setEditableProductId(null);
    }
  };

  const toggleEditProduct = () => {
    setShowEditProduct(!showEditProduct);
  };

  useEffect(() => {
    fetchProducts(); // Fetch all products initially
    fetchCategory(); // Fetch categories initially
  }, []);
  const fetchProductByCategory = useCallback(async () => {
    if (!selectedCategory) {
      fetchProducts();
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/products/all-products-by-category/${selectedCategory}`
      );

      if (!response.ok) throw new Error("Failed to fetch products.");
      const data = await response.json();

      setProducts(data.allProducts);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching products.");
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchProductByCategory(); // Fetch products by selected category
  }, [selectedCategory, fetchProductByCategory]); // Trigger fetch when selectedCategory changes

  const handleEditProductClick = (productId) => {
    setEditableProductId(productId);
    toggleEditProduct();
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/products/delete-product/${productId}`,
          { method: "DELETE" }
        );
        if (!response.ok) throw new Error("Failed to delete the product.");
        toast.success("Product deleted successfully!");
        fetchProducts();
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Error deleting the product.");
      }
    }
  };

  const handleToggleStatus = async (productId, field, value) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/products/update-status/${productId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ [field]: !value }),
        }
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to update the product.");
      toast.success("Product updated successfully!");
      // fetchProducts();
      fetchProductByCategory();
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.message || "Error updating the product.");
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/products/all-products`
      );

      if (!response.ok) throw new Error("Failed to fetch products.");
      const data = await response.json();
      setProducts(data.allProducts);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching products.");
    }
  };

  const fetchCategory = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/category/all-category`
      );
      if (!response.ok) throw new Error("Failed to fetch categories.");
      const data = await response.json();

      setCategories(data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching categories.");
    }
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : "Unknown";
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className="mb-10 flex justify-between rounded-md bg-white p-5">
        <div className="flex gap-5">
          <button className="flex items-center rounded-md bg-primary p-2 text-primaryText">
            Import
          </button>
          <button className="flex items-center rounded-md bg-primary p-2 text-primaryText">
            Export
          </button>
        </div>
        <button
          className="flex items-center rounded-md bg-primary p-2 text-primaryText"
          onClick={toggleAddProduct}
        >
          Add Product
        </button>
      </div>

      {showAddProduct && (
        <AddProductModal
          toggleAddProduct={toggleAddProduct}
          showAddProduct={showAddProduct}
          onProductAdded={fetchProducts}
          productId={editableProductId}
        />
      )}

      {showEditProduct && (
        <EditProductModal
          toggleEditProduct={toggleEditProduct}
          showEditProduct={showEditProduct}
          onProductUpdated={fetchProducts}
          productId={editableProductId}
        />
      )}

      {/* Category Dropdown */}
      <div className="mb-6">
        <label
          htmlFor="category"
          className="mb-1 block text-sm font-medium text-gray-700"
        >
          Category
        </label>
        <select
          id="category"
          className="focus:ring-primary-500 focus:border-primary-500 block w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base shadow-sm transition-all focus:outline-none focus:ring-2"
          value={selectedCategory}
          onChange={(e) => handleCategoryChange(e)}
        >
          <option value="">All Categories</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id} className="py-2">
              {category.name} ({category.productCount})
            </option>
          ))}
        </select>
      </div>

      {/* Search Box */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search Products"
          className="focus:ring-primary-500 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base shadow-sm focus:outline-none focus:ring-2"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>

      {/* Product Table */}
      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-left text-sm text-gray-500">
          <thead className="bg-primary text-xs uppercase text-primaryText">
            <tr>
              <th className="px-6 py-3">Product Image</th>
              <th className="px-6 py-3">Product Name</th>
              <th className="px-6 py-3">Category</th>
              <th className="px-6 py-3">Price</th>
              <th className="px-6 py-3">Stock</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Featured</th>
              <th className="px-6 py-3">Best Selling</th>
              <th className="px-6 py-3">Active</th>
              <th className="px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((product, index) => (
              <tr key={index} className="border-b bg-white">
                <td className="px-6 py-4">
                  <img
                    src={`${process.env.REACT_APP_API_URL_Plain}/config/uploads/products/product_images/${product.product_images[0]}`}
                    alt="Product"
                    className="h-10 w-10 object-cover"
                  />
                </td>
                <td className="px-6 py-4 text-base text-navy-900">
                  {product.name}
                </td>
                <td className="px-6 py-4 text-base text-navy-900">
                  {getCategoryName(product.category_id)}
                </td>
                <td className="px-6 py-4 text-base text-navy-900">
                  {product.price_customer} TK
                </td>
                <td className="px-6 py-4 text-base text-navy-900">
                  {product.quantity}
                </td>
                <td className="px-6 py-4">
                  <span
                    className={
                      product.quantity > 0 ? "text-primary" : "text-red-500"
                    }
                  >
                    {product.quantity > 0 ? "In Stock" : "Out of Stock"}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <label className="inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={product.isfeatured}
                      onChange={() =>
                        handleToggleStatus(
                          product.id,
                          "isfeatured",
                          product.isfeatured
                        )
                      }
                    />
                    <div className="peer relative h-6 w-11 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-0.5 after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-checked:bg-primary dark:peer-focus:ring-blue-800 rtl:peer-checked:after:-translate-x-full"></div>
                  </label>
                </td>
                <td className="px-6 py-4">
                  <label className="inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={product.isbestselling}
                      onChange={() =>
                        handleToggleStatus(
                          product.id,
                          "isbestselling",
                          product.isbestselling
                        )
                      }
                    />
                    <div className="peer relative h-6 w-11 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-0.5 after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-checked:bg-primary dark:peer-focus:ring-blue-800 rtl:peer-checked:after:-translate-x-full"></div>
                  </label>
                </td>
                <td className="px-6 py-4">
                  <label className="inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={product.isactive}
                      onChange={() =>
                        handleToggleStatus(
                          product.id,
                          "isactive",
                          product.isactive
                        )
                      }
                    />
                    <div className="peer relative h-6 w-11 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-0.5 after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-checked:bg-primary dark:peer-focus:ring-blue-800 rtl:peer-checked:after:-translate-x-full"></div>
                  </label>
                </td>
                <td className="px-6 py-4">
                  <button
                    aria-label="Edit Product"
                    className="p-1 text-2xl hover:bg-gray-100"
                    onClick={() => handleEditProductClick(product.id)}
                  >
                    <MdEdit />
                  </button>
                  <button
                    className="p-1 text-2xl text-red-600 hover:bg-gray-100"
                    onClick={() => handleDeleteProduct(product.id)}
                  >
                    <MdDelete />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
